import React, { useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Alert, Button, Col, Form, Pagination, Row } from "react-bootstrap";
import { MdAdd, MdDelete, MdEdit } from "react-icons/md";
import { AuthContext } from "../../../contexts/AuthContext";
import { tempUrl } from "../../../contexts/ContextProvider";
import {
  ConfirmDeleteDialog,
  InfoDialog,
  Loader,
  ScrollToTop,
  SearchBar,
} from "../../../components";
import { ShowTableKategori } from "../../../components/ShowTable";

const TampilKategori = () => {
  const { user, dispatch } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const selectedId = location.pathname.split("/")[2];

  const [kategoris, setKategoris] = useState([]);
  const [selectedKategori, setSelectedKategori] = useState(null);
  const [query, setQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [totalPage, setTotalPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const handleAuthError = useCallback(
    (err) => {
      if (err.response?.status === 401) {
        dispatch({ type: "LOGOUT" });
        navigate("/");
      }
    },
    [dispatch, navigate]
  );

  const getKategoris = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${tempUrl}/kategorisPagination?search_query=${encodeURIComponent(
          searchTerm
        )}&page=${page}&limit=20`,
        {
          _id: user.id,
          token: user.token,
        }
      );
      setKategoris(response.data.kategoris);
      setTotalPage(response.data.totalPage);
      setTotalRows(response.data.totalRows);
    } catch (err) {
      handleAuthError(err);
      setError(err.response?.data?.message || "Gagal mengambil data kategori.");
    }
    setLoading(false);
  }, [handleAuthError, page, searchTerm, user.id, user.token]);

  const getKategoriById = useCallback(async () => {
    if (!selectedId) {
      setSelectedKategori(null);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${tempUrl}/kategoris/${selectedId}`, {
        _id: user.id,
        token: user.token,
      });
      setSelectedKategori(response.data);
    } catch (err) {
      handleAuthError(err);
      setError(err.response?.data?.message || "Gagal mengambil detail kategori.");
    }
    setLoading(false);
  }, [handleAuthError, selectedId, user.id, user.token]);

  useEffect(() => {
    getKategoris();
  }, [getKategoris]);

  useEffect(() => {
    getKategoriById();
  }, [getKategoriById]);

  const searchData = (event) => {
    event.preventDefault();
    setPage(0);
    setSearchTerm(query);
  };

  const deleteKategori = async () => {
    setShowDeleteConfirm(false);
    setLoading(true);
    try {
      await axios.post(`${tempUrl}/deleteKategori/${selectedId}`, {
        _id: user.id,
        token: user.token,
      });
      navigate("/kategori");
      setSelectedKategori(null);
      await getKategoris();
    } catch (err) {
      handleAuthError(err);
      setDeleteError(err.response?.data?.message || "Gagal menghapus kategori.");
      setLoading(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div>
      <ScrollToTop />
      <div className="page-header">
        <div>
          <div className="page-kicker">Master</div>
          <h1 className="page-title">Kategori</h1>
        </div>
        <Button
          className="btn-icon"
          size="sm"
          onClick={() => navigate("/kategori/tambahKategori")}
        >
          <MdAdd /> Tambah
        </Button>
      </div>
      {error && <Alert variant="warning">{error}</Alert>}

      {selectedKategori && (
        <section className="section-panel">
          <div className="toolbar mt-0">
            <div className="detail-title-actions">
              <Button
                className="btn-back"
                size="sm"
                variant="outline-secondary"
                onClick={() => navigate("/kategori")}
              >
                {"< Kembali"}
              </Button>
              <div>
                <h2 className="h6 fw-bold mb-1">Detail Kategori</h2>
                <div className="text-muted">{selectedKategori.nama}</div>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Button
                className="btn-icon"
                size="sm"
                variant="outline-primary"
                onClick={() =>
                  navigate(`/kategori/${selectedKategori.id}/edit`)
                }
              >
                <MdEdit /> Ubah
              </Button>
              <Button
                className="btn-icon"
                size="sm"
                variant="outline-danger"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <MdDelete /> Hapus
              </Button>
            </div>
          </div>
          <hr />
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <div className="detail-label">Kode</div>
                <div className="detail-value">
                  {selectedKategori.kode || "-"}
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <div className="detail-label">Nama</div>
                <div className="detail-value">
                  {selectedKategori.nama || "-"}
                </div>
              </div>
            </Col>
          </Row>
        </section>
      )}

      <Form onSubmit={searchData}>
        <div className="toolbar">
          <div className="d-flex gap-2 flex-wrap">
            <SearchBar setSearchTerm={setQuery} value={query} />
            <Button className="btn-icon" type="submit">
              Cari
            </Button>
          </div>
          <div className="text-muted small">Total data: {totalRows}</div>
        </div>
      </Form>

      <ShowTableKategori
        kategoris={kategoris}
        selectedId={selectedId}
        onSelect={(id) => navigate(`/kategori/${id}`)}
      />

      {totalPage > 1 && (
        <Pagination className="mt-3">
          <Pagination.Prev
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
          />
          {Array.from({ length: totalPage }, (_, index) => (
            <Pagination.Item
              key={index}
              active={index === page}
              onClick={() => setPage(index)}
            >
              {index + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next
            disabled={page + 1 >= totalPage}
            onClick={() => setPage(page + 1)}
          />
        </Pagination>
      )}

      <ConfirmDeleteDialog
        show={showDeleteConfirm}
        message={`Yakin ingin menghapus kategori ${selectedKategori?.nama}?`}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={deleteKategori}
      />
      <InfoDialog
        show={Boolean(deleteError)}
        title="Data Tidak Bisa Dihapus"
        message={deleteError}
        onClose={() => setDeleteError("")}
      />
    </div>
  );
};

export default TampilKategori;
